import React from 'react';
import { MapPin, Phone, Clock, Navigation, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import Reveal from '../components/Reveal';
import { images, stores } from '../mock';

const PageHero = ({ eyebrow, title, subtitle, image }) => (
  <section className="relative h-[46vh] min-h-[340px] flex items-center justify-center overflow-hidden">
    <div className="absolute inset-0 bg-cover bg-center slide-zoom" style={{ backgroundImage: `url(${image})` }} />
    <div className="absolute inset-0 bg-gradient-to-b from-[#0f2c1f]/90 via-[#0f2c1f]/70 to-[#0f2c1f]/90" />
    <div className="relative z-10 text-center px-5 pt-16 max-w-3xl">
      <p className="animate-fade-up text-[#dcbf74] text-xs md:text-sm font-semibold tracking-[0.35em] mb-4">{eyebrow}</p>
      <h1 className="animate-fade-up font-serif-display text-white text-4xl md:text-6xl font-bold text-shadow-lg" style={{ animationDelay: '0.15s' }}>{title}</h1>
      {subtitle && <p className="animate-fade-up text-white/80 mt-4" style={{ animationDelay: '0.25s' }}>{subtitle}</p>}
    </div>
  </section>
);

const Stores = () => {
  return (
    <div>
      <PageHero
        eyebrow="RETAIL LOCATIONS"
        title="Our Stores"
        subtitle="Experience premium shopping across our flagship destinations in New South Wales."
        image={images.storeInterior}
      />

      <section className="py-20 md:py-28 bg-[#faf7f0]">
        <div className="max-w-6xl mx-auto px-5 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {stores.map((s, i) => (
              <Reveal key={s.id} delay={i * 100}>
                <div className="bg-white rounded-3xl overflow-hidden shadow-sm border border-[#e8e0cf] lift-card h-full flex flex-col">
                  <div className="relative h-64 overflow-hidden">
                    {s.mapEmbed && (
                      <iframe
                        title={`${s.name} map`}
                        src={s.mapEmbed}
                        width="100%"
                        height="100%"
                        style={{ border: 0 }}
                        allowFullScreen=""
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                      />
                    )}
                    <span className="absolute top-4 left-4 z-10 px-4 py-1.5 rounded-full bg-[#c8a24c] text-[#0f2c1f] text-xs font-semibold tracking-widest shadow-md">{s.tag}</span>
                  </div>
                  <div className="p-7 flex flex-col flex-1">
                    <h3 className="font-serif-display text-[#163a29] text-2xl font-bold mb-4">{s.name}</h3>
                    <ul className="space-y-3 mb-6 flex-1">
                      <li className="flex gap-3 text-[#3a4a42]">
                        <MapPin size={18} className="text-[#c8a24c] shrink-0 mt-0.5" />
                        <span>{s.address}</span>
                      </li>
                      <li className="flex gap-3 text-[#3a4a42] items-center">
                        <Phone size={18} className="text-[#c8a24c] shrink-0" />
                        <a href={`tel:${s.phone}`} className="hover:text-[#163a29]">{s.phone}</a>
                      </li>
                      <li className="flex gap-3 text-[#3a4a42] items-center">
                        <Clock size={18} className="text-[#c8a24c] shrink-0" />
                        <span>{s.hours}</span>
                      </li>
                    </ul>
                    <a
                      href={s.mapUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[#163a29] text-white font-semibold hover:bg-[#0f2c1f] transition-all duration-300"
                    >
                      <Navigation size={17} />
                      Launch Navigation
                    </a>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          {/* Interior gallery */}
          <Reveal className="mt-20">
            <div className="text-center mb-10">
              <p className="text-[#c8a24c] text-sm font-semibold tracking-[0.3em] mb-3 uppercase">The Interior Selections</p>
              <h2 className="font-serif-display text-[#163a29] text-3xl md:text-4xl font-bold">Explore Our Curated Collections</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[images.spices3, images.produce, images.rice, images.sweets].map((img, i) => (
                <div key={i} className="img-zoom-parent rounded-2xl overflow-hidden h-44 md:h-56">
                  <img src={img} alt="store interior" className="img-zoom w-full h-full object-cover" />
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal className="mt-16 text-center">
            <Link to="/contact" className="group inline-flex items-center gap-2 px-9 py-4 rounded-full bg-[#c8a24c] text-[#0f2c1f] font-semibold hover:bg-[#dcbf74] transition-all duration-300">
              Contact Our Team
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Stores;
